import React, { useEffect, useState } from "react";
import { Timer, StopCircle } from "@phosphor-icons/react";

/**
 * CallTimer — affiche le temps écoulé depuis le début de l'appel courant.
 * S'auto-démarre quand `startAt` (timestamp ms) est fourni. Bouton stop optionnel.
 *
 * Props:
 *   - startAt: number|null — timestamp ms du début. null = pas d'appel en cours
 *   - onStop: () => void — callback quand l'utilisateur clique sur ⏹
 *   - compact: bool — variante réduite pour intégration dans HUD
 */
function fmt(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function CallTimer({ startAt, onStop, compact = false }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startAt) { setElapsed(0); return; }
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startAt) / 1000)));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [startAt]);

  if (!startAt) return null;

  const isLong = elapsed > 180; // > 3 min = conversation engagée
  const cls = isLong
    ? "bg-primary/15 text-primary border-primary/40"
    : "bg-secondary/10 text-secondary border-secondary/40";

  if (compact) {
    return (
      <div
        className={`inline-flex items-center gap-1.5 border rounded-full px-2.5 py-1 text-xs font-mono font-bold tabular-nums ${cls}`}
        data-testid="call-timer-compact"
      >
        <Timer size={12} weight={isLong ? "fill" : "duotone"} className="animate-pulse"/>
        <span>{fmt(elapsed)}</span>
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-2 border rounded-lg px-3 py-1.5 ${cls}`}
      data-testid="call-timer"
    >
      <Timer size={16} weight={isLong ? "fill" : "duotone"} className="animate-pulse"/>
      <div className="flex flex-col leading-tight">
        <span className="text-[9px] uppercase tracking-widest font-bold opacity-80">Appel en cours</span>
        <span className="text-base font-mono font-bold tabular-nums">{fmt(elapsed)}</span>
      </div>
      {onStop && (
        <button
          type="button"
          onClick={onStop}
          className="ml-1 opacity-70 hover:opacity-100 transition-opacity"
          data-testid="call-timer-stop"
          title="Terminer l'appel"
        >
          <StopCircle size={18} weight="fill"/>
        </button>
      )}
    </div>
  );
}
